import { formatDateTime, formatMonthDayTime, parseUtcMs } from "@/utils/datetime";
import {
  AnchoredPopup,
  IconButton,
  LabelChip,
  Scrollbar,
  Text,
  useAnchoredPopupAnchor,
} from "@carbon6/c6-frontend-design";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import {
  isEpisodeExcluded,
  type EpisodeSnapshot,
  type EpisodeSummary,
  type EpisodeTerm,
} from "@/api";
import { hatcheryTextModes, useThemeModePreference } from "@/app/theme";
import {
  Keycap,
  hasModifierKey,
  isEditableKeyboardTarget,
  isHotkeySurfaceActive,
  isNativeEnterTarget,
  isRangeInput,
} from "@/components/keyboard";
import {
  EPISODE_INVALID_REASON_TEXT,
  EPISODE_VALIDATION_TEXT,
  episodeFailureTip,
  episodeQualityTagChipColor,
  episodeQualityTagLabel,
  episodeQualityTagText,
  episodeQualityTagTip,
} from "@/components/label";
import { RefreshIcon } from "@/components/icons";
import { HatTextTooltip, HatTooltip } from "@/components/tooltip";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import {
  commentExceedsLimit,
  commentSaveLength,
  type EpisodeReviewDraft,
} from "./episodeAnnotationDraft";
import { EpisodeLabelGuideAction } from "./EpisodeLabelGuideAction";
import { EpisodeTermEditor, episodeTermLines, episodeTermSummary } from "./EpisodeTermEditor";
import { EpisodeReviewModeSwitcher } from "./EpisodeReviewModeSwitcher";
import { episodeReviewModeStore, useEpisodeReviewMode } from "./episodeReviewModeStore";
import {
  ACCEPT_KEY,
  FAILURE_KEY,
  MODE_KEY,
  TAG_HOTKEYS,
  TAG_HOTKEY_INDEX,
} from "./episodeTagHotkeys";
import styles from "./EpisodeReviewPanel.module.css";

// 액션 단축키. `TAG_HOTKEYS`(순번 태그)와 겹치지 않는 글자만 고른다. 저장의 기본 키는 Enter 다.
// S 는 손을 홈 포지션에 둔 채 저장하기 위한 보조 키다. Ctrl/Cmd+S(브라우저 저장)는 수식키 가드가
// 거른다.
const EXCLUDE_KEY = "x";
const COMMENT_KEY = "c";
const TERMS_KEY = "k";
const SAVE_KEY = "s";

// 제외 버튼 툴팁. 괄호 안은 목록 필터 query 파라미터다(줄 라벨의 `한글(파라미터)` 규칙과 같다).
const EXCLUDE_TIP =
  "이 에피소드를 Training에서 뺍니다(excluded). 실패로 표시해도 자동으로 켜지지 않습니다.";

export type EpisodeReviewPanelProps = {
  active?: boolean;
  // 저장값이다. 검증·사유·스냅샷·검토 완료를 여기서 읽는다. 편집값은 `draft` 다.
  row: EpisodeSummary;
  draft: EpisodeReviewDraft;
  // 결함 태그 어휘·표시 순서(GET /v1/review-options 의 episode_quality_tags). 이 순서가 순번 키 순서다.
  qualityTags: readonly string[];
  // 코멘트 최대 길이(같은 옵션 계약). 저장 전에 확인한다.
  commentMaxLength: number;
  busy: boolean;
  // 저장하지 않은 편집이 있는가. 리뷰에서는 키로 빠르게 진행하므로 "눌렀는데 서버에 저장되지
  // 않았다"를 눈으로 알 수 있어야 한다. 독 배경·배지·저장 버튼 강조 세 가지로 표시한다.
  dirty?: boolean;
  // 최신 상태를 불러오는 중이거나 불러오지 못했으면 입력과 저장을 막는다.
  loading?: boolean;
  loadError?: boolean;
  message?: string | null;
  // 409 뒤에는 서버에 저장된 코멘트를 함께 보여 준다. 사용자가 두 값을 비교하고 다시 저장한다.
  conflicted?: boolean;
  // 저장이 제외 변경 때문에 거절되어 제외 변경만 막는다(서버 판정 409·503). 이미 바꾼 제외를
  // 되돌리는 것은 막지 않는다. 막으면 저장할 방법이 없다.
  excludedLocked?: boolean;
  // 제외 토글 옆 안내다. 잠근 사유이거나, 학습이 이 Dataset 을 쓰는 중일 수 있다는 힌트다. 힌트는
  // 안내만 하고 토글을 막지 않는다.
  excludedNote?: string;
  onRetry?: () => void;
  onSetExcluded: (excluded: boolean) => void;
  onToggleFailure: () => void;
  onToggleAcceptance: () => void;
  onToggleTag: (tag: string) => void;
  // 표기 한 건을 더한다. 소비처가 `withTermAdded` 로 draft 에 반영한다.
  onAddTerm: (termKey: string, termValue: string) => void;
  // 표기 한 건을 지운다. 소비처가 `withTermRemoved` 로 draft 에 반영한다.
  onRemoveTerm: (term: EpisodeTerm) => void;
  onSetComment: (comment: string) => void;
  onSave: () => void;
};

/**
 * 에피소드 라벨링 독. 머리 줄과 본문 두 층이다.
 *
 * **머리 줄은 독의 스크롤 밖에 둔다.** 높이가 모자라면 독 본문이 먼저 줄고 자기 안에서 스크롤한다.
 * 그래도 모드 스위처·검토 상태·`저장·검토 완료` 는 늘 보인다. 판정을 끝내는 동작이 스크롤 뒤로
 * 숨으면 안 된다.
 *
 * **저장은 `저장·검토 완료` 하나뿐이다.** 수정 내용이 없어도 누를 수 있고, 누르면 검토 완료가
 * 기록된다. 모든 편집은 draft 이고 이 동작이 한 번에 저장한다. 결함 수용과 코멘트도 같다.
 *
 * **모드는 머리 줄이 소유한다.** 뷰 모드는 독의 최소화, 리뷰 모드는 최대화다.
 * - 뷰 모드: 무엇이 적혀 있는지 한 줄 요약과 `실패 표시`·`제외` 두 토글. 영상을 넓게 보면서 빠르게
 *   판정하고 넘기는 흐름이다. 보이지 않는 축(결함·수용·표기)의 단축키는 끈다.
 * - 리뷰 모드: 결과·제외·검증(결함 수용) / 결함 / 표기 / 코멘트.
 *
 * **줄 라벨은 `한글(필터 파라미터)` 다.** 괄호 안을 목록 필터 query 파라미터와 같게 한다. 그래서
 * 화면에서 좁힌 결과를 MCP·API 응답과 그대로 대조할 수 있다.
 *
 * **이 화면의 목적은 손을 키보드에, 눈을 영상에 둔 채로 진행하는 것이다.** 그래서 모든 액션에
 * 키를 하나씩 주고, 버튼에 그 키를 표시한다. 키 표는 `episodeTagHotkeys.ts` 가 소유한다. 코멘트
 * 입력 안에서는 단축키가 동작하지 않는다. `Esc` 로 입력에서 나오고, `Ctrl/⌘+Enter` 로 저장한다.
 */
export function EpisodeReviewPanel({
  active = true,
  row,
  draft,
  qualityTags,
  commentMaxLength,
  busy,
  dirty = false,
  loading = false,
  loadError = false,
  message = null,
  conflicted = false,
  excludedLocked = false,
  excludedNote,
  onRetry,
  onSetExcluded,
  onToggleFailure,
  onToggleAcceptance,
  onToggleTag,
  onAddTerm,
  onRemoveTerm,
  onSetComment,
  onSave,
}: EpisodeReviewPanelProps): ReactNode {
  const mode = useEpisodeReviewMode();
  const viewMode = mode === "view";
  const rootRef = useRef<HTMLElement>(null);
  const commentRef = useRef<HTMLTextAreaElement>(null);
  // 뷰 모드에서 `C` 를 누르면 리뷰 모드로 바꾼 뒤 입력에 포커스를 준다. 입력은 모드가 바뀐 다음
  // 렌더에 생긴다. 그래서 그 렌더 뒤에 포커스를 옮긴다.
  const focusCommentRef = useRef(false);
  const termsTriggerRef = useRef<HTMLButtonElement>(null);
  const termsPopup = useAnchoredPopupAnchor();
  const [termsOpen, setTermsOpen] = useState(false);
  const inputsDisabled = busy || loading || loadError;
  const commentLength = commentSaveLength(draft.comment);
  const commentTooLong = commentExceedsLimit(draft, row, commentMaxLength);
  const saveDisabled = inputsDisabled || commentTooLong;
  // 되돌리기는 막지 않는다. 학습 잠금을 알기 전에 바꾼 제외를 되돌릴 수 없으면 저장할 방법이 없다.
  const excludeDisabled =
    inputsDisabled || (excludedLocked && draft.excluded === isEpisodeExcluded(row.status));
  // 수용은 검증이 무효인 에피소드에서만 새로 켤 수 있다. 이미 수용한 에피소드는 해제할 수 있다.
  // 채널 누락 에피소드는 새로 수용할 수 없다(서버 409 `episode.invalid_reason_not_acceptable`). 파일을
  // 채워 재검사해야 판정이 바뀐다. 사유는 목록 행과 단건 조회가 싣는다. 그 전에는 입력이 잠겨 있다.
  const reasonNotAcceptable = row.invalid_reason === "missing_channel" && !row.accepted_invalid;
  const canAccept =
    (row.validation_status === "invalid" || row.accepted_invalid) && !reasonNotAcceptable;
  const acceptDisabled = inputsDisabled || !canAccept;

  // 표기 팝오버를 연다. 트리거 사각형을 앵커로 쓴다. 그래서 키(`K`)로 열든 클릭으로 열든 같은
  // 위치에 뜬다. 키로 연 팝오버가 화면 구석에 나타나면 사용자는 보던 위치를 놓친다.
  const openTerms = (): void => {
    const trigger = termsTriggerRef.current;
    if (trigger !== null) {
      termsPopup.setAnchor({ type: "rect", rect: trigger.getBoundingClientRect(), el: trigger });
    }
    setTermsOpen(true);
  };

  const closeTerms = (): void => {
    setTermsOpen(false);
    termsPopup.close();
  };

  const focusComment = (): void => {
    if (viewMode) {
      focusCommentRef.current = true;
      episodeReviewModeStore.set("review");
      return;
    }
    commentRef.current?.focus();
  };

  useEffect(() => {
    if (!viewMode && focusCommentRef.current) {
      focusCommentRef.current = false;
      commentRef.current?.focus();
    }
  }, [viewMode]);

  // 최신 핸들러·상태를 effect 재구독 없이 참조한다. 그래서 draft 가 바뀔 때마다 리스너를 다시
  // 등록하지 않는다.
  const hotkeyRef = useRef({
    viewMode,
    qualityTags,
    inputsDisabled,
    saveDisabled,
    excludeDisabled,
    acceptDisabled,
    excluded: draft.excluded,
    termsOpen,
    openTerms,
    focusComment,
    onToggleTag,
    onSetExcluded,
    onToggleFailure,
    onToggleAcceptance,
    onSave,
  });
  hotkeyRef.current = {
    viewMode,
    qualityTags,
    inputsDisabled,
    saveDisabled,
    excludeDisabled,
    acceptDisabled,
    excluded: draft.excluded,
    termsOpen,
    openTerms,
    focusComment,
    onToggleTag,
    onSetExcluded,
    onToggleFailure,
    onToggleAcceptance,
    onSave,
  };

  useEffect(() => {
    if (!active) return;
    function handleKeyDown(event: globalThis.KeyboardEvent): void {
      if (event.defaultPrevented) return;
      if (hasModifierKey(event)) return;
      // 타임라인 슬라이더는 입력이지만 글자·숫자를 처리하지 않는다. 타임라인을 클릭한 뒤에도 독 키가
      // 동작해야 사용자가 영상에서 눈을 떼지 않고 판정을 계속할 수 있다.
      if (isEditableKeyboardTarget(event.target) && !isRangeInput(event.target)) return;
      if (!isHotkeySurfaceActive(rootRef.current)) return;

      const current = hotkeyRef.current;
      // 표기 팝오버가 열려 있는 동안에는 독 단축키를 모두 무시한다. 팝오버는 모달이 아니므로
      // 뒤를 `inert` 로 잠그지 않는다. 안에 입력(키·값)이 있어서 글자 키가 겹친다.
      if (current.termsOpen) return;
      const key = event.key.toLowerCase();
      // 모드 토글은 편집이 아니라 화면 배치다. 그래서 저장 중에도, 뷰 모드에서도 동작한다.
      if (key === MODE_KEY) {
        event.preventDefault();
        episodeReviewModeStore.toggle();
        return;
      }
      // 코멘트 입력으로 옮긴다. 뷰 모드면 리뷰 모드로 바꾼 뒤 옮긴다. 기본 동작을 막아야 옮긴
      // 입력에 `c` 가 입력되지 않는다.
      if (key === COMMENT_KEY) {
        event.preventDefault();
        current.focusComment();
        return;
      }
      // 표기 열기. 뷰 모드에는 줄이 없어 앵커를 정할 수 없다. 그래서 끈다.
      if (key === TERMS_KEY) {
        if (current.viewMode) return;
        event.preventDefault();
        current.openTerms();
        return;
      }
      if (current.inputsDisabled) return;

      if (event.key === "Enter" || key === SAVE_KEY) {
        // 진단 열·재생 블록의 버튼(칩·확대·보기 스위처)에 포커스가 있으면 Enter 는 그 버튼만 누른다.
        // 칩을 누른 뒤 Enter 가 저장하고 다음 에피소드로 이동하면 보던 구간을 놓친다. `S` 는 그대로다.
        if (event.key === "Enter" && isNativeEnterTarget(event.target)) return;
        event.preventDefault();
        if (!current.saveDisabled) current.onSave();
        return;
      }
      if (key === EXCLUDE_KEY) {
        if (current.excludeDisabled) return;
        event.preventDefault();
        current.onSetExcluded(!current.excluded);
        return;
      }
      if (key === FAILURE_KEY) {
        event.preventDefault();
        current.onToggleFailure();
        return;
      }
      // 여기부터는 리뷰 모드에만 있는 축이다. 보이지 않는 칩이 토글되면 사용자는 무엇을 바꿨는지
      // 모르고, 미저장 편집만 남는다.
      if (current.viewMode) return;
      if (key === ACCEPT_KEY) {
        if (current.acceptDisabled) return;
        event.preventDefault();
        current.onToggleAcceptance();
        return;
      }
      const index = TAG_HOTKEY_INDEX.get(key);
      if (index === undefined) return;
      const tag = current.qualityTags[index];
      if (tag === undefined) return;
      event.preventDefault();
      current.onToggleTag(tag);
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [active]);

  const failureToggle = (
    <HatTextTooltip text={episodeFailureTip(true)} placement="top">
      <IconButton
        text="실패 표시"
        size="md"
        variant="surface"
        // 판정 축이므로 tone 은 danger 가 아니다. 빨강은 제외(조치)가 쓴다.
        // 켜짐만 accent 로 바꿔 "무엇을 골랐나"를 색 하나로 알 수 있게 한다.
        tone={draft.task_failed ? "primary" : "neutral"}
        selected={draft.task_failed}
        disabled={inputsDisabled}
        onClick={onToggleFailure}
      >
        <Keycap>{FAILURE_KEY.toUpperCase()}</Keycap>
      </IconButton>
    </HatTextTooltip>
  );

  // 버튼 문구가 축 이름을 겸한다. 그래서 줄 라벨을 따로 두지 않고, 필터 파라미터는 툴팁이 알린다.
  const excludeToggle = (
    <HatTextTooltip text={EXCLUDE_TIP} placement="top">
      <IconButton
        text={draft.excluded ? "Training에서 제외됨" : "이 에피소드 제외"}
        size="md"
        variant="surface"
        // 켜졌을 때만 danger 다. 꺼짐에도 danger 를 주면 c6 가 전경색까지 바꿔 "이미 제외됨"처럼
        // 읽힌다. 그러면 두 상태가 테두리 하나로만 구분된다.
        tone={draft.excluded ? "danger" : "neutral"}
        selected={draft.excluded}
        disabled={excludeDisabled}
        onClick={() => onSetExcluded(!draft.excluded)}
      >
        <Keycap>{EXCLUDE_KEY.toUpperCase()}</Keycap>
      </IconButton>
    </HatTextTooltip>
  );

  return (
    <section
      ref={rootRef}
      className={styles.dock}
      data-dirty={dirty ? "true" : undefined}
      aria-label="에피소드 라벨링"
      aria-busy={loading || busy}
    >
      <div className={styles.head}>
        {/* 모드 스위처가 제목을 대신한다. 이름은 이 `section` 의 aria-label 과 스위처의
            그룹 라벨이 제공한다. */}
        <EpisodeReviewModeSwitcher />
        <ReviewState row={row} />
        {/* 뷰 모드에는 검증 줄이 없다. 무효일 때만 머리 줄이 사유를 알린다. */}
        {viewMode && row.validation_status === "invalid" ? (
          <span className={styles.headNote}>{validationText(row)}</span>
        ) : null}
        {/* 서버에 아직 저장되지 않은 편집. 키로 빠르게 진행할 때 저장을 빠뜨렸음을 알리는 신호다. */}
        {dirty ? <span className={`${styles.badge} ${styles.badgeDirty}`}>저장 안 됨</span> : null}
        {/* 이 줄의 오른쪽 끝. margin 은 버튼이 아니라 이 슬롯에 적용한다. 버튼에 margin 을 적용하면
            래퍼가 flex item 이라 효과가 없다. */}
        <div className={styles.headActions}>
          <EpisodeLabelGuideAction qualityTags={qualityTags} />
          <SaveAction busy={busy} dirty={dirty} disabled={saveDisabled} onSave={onSave} />
        </div>
      </div>

      <Scrollbar className={styles.bodyScroll} gutter="auto" aria-label="에피소드 판정 스크롤">
        <div className={styles.body}>
          {/* 독 바탕이 sunken 이다. 그래서 알림은 한 단계 위 면(surface)을 깐다. */}
          {loading ? (
            <LoadingState size="sm" mode="surface">
              최신 리뷰 상태를 불러오고 있습니다.
            </LoadingState>
          ) : null}
          {loadError ? (
            <EmptyState
              size="sm"
              mode="surface"
              tone="error"
              action={
                onRetry === undefined
                  ? undefined
                  : { label: "다시 시도", onClick: onRetry, icon: <RefreshIcon /> }
              }
            >
              리뷰 상태를 불러오지 못했습니다. 다시 시도해 주세요.
            </EmptyState>
          ) : null}
          {message ? (
            <p className={styles.feedback} role="status">
              {message}
            </p>
          ) : null}

          {viewMode ? (
            <>
              <div className={styles.viewRow}>
                <Text variant={hatcheryTextModes["body-normal"]} className={styles.muted}>
                  {reviewSummary(draft)}
                </Text>
                <div className={styles.viewActions}>
                  {failureToggle}
                  {excludeToggle}
                </div>
              </div>
              {excludedNote ? <p className={styles.note}>{excludedNote}</p> : null}
              {row.accepted_invalid_stale === true || row.review_comment_stale === true ? (
                <p className={styles.warning}>
                  결함 수용이나 코멘트를 남긴 뒤 에피소드 상태가 바뀌었습니다. 리뷰 모드에서 확인해
                  주세요.
                </p>
              ) : null}
            </>
          ) : (
            <>
              {/* 결과·제외·검증을 한 줄에 둔다. 셋 다 값 하나와 버튼 하나뿐이다.
                  - 제외는 버튼 문구가 축 이름을 겸하므로 줄 라벨이 없다. 제외는 판정이 아니라 Training
                    에서 빼는 조치다. 실패해도 자동으로 켜지지 않는다. 실패 에피소드를 실패 인식
                    Training 에 쓰는 것이 정상이다.
                  - 검증은 자동 판정이다. 사람은 무효 판정을 수용할 수 있다. 수용해도 자동 판정과 소견은
                    바뀌지 않는다. */}
              <div className={styles.row}>
                <AxisLabel ko="결과" param="task_failed" />
                <div className={styles.controls}>
                  {failureToggle}
                  <span className={styles.rowDivider} aria-hidden="true" />
                  {excludeToggle}
                  <span className={styles.rowDivider} aria-hidden="true" />
                  <AxisLabel ko="검증" param="validation_status" inline />
                  <span className={styles.value}>{validationText(row)}</span>
                  <HatTextTooltip
                    text={
                      canAccept
                        ? "결함을 수용하면 결함에 따른 사용 제한이 해제됩니다. 자동 판정과 소견은 유지됩니다."
                        : reasonNotAcceptable
                          ? "채널 누락 에피소드는 결함을 수용할 수 없습니다. 빠진 채널 파일을 채운 뒤 재검사해야 합니다."
                          : "검증이 무효인 에피소드만 수용할 수 있습니다."
                    }
                    placement="top"
                  >
                    <IconButton
                      text={draft.accepted_invalid ? "결함 수용함" : "결함 수용"}
                      size="md"
                      variant="surface"
                      tone={draft.accepted_invalid ? "primary" : "neutral"}
                      selected={draft.accepted_invalid}
                      disabled={acceptDisabled}
                      onClick={onToggleAcceptance}
                    >
                      <Keycap>{ACCEPT_KEY.toUpperCase()}</Keycap>
                    </IconButton>
                  </HatTextTooltip>
                  <SnapshotNote
                    label="수용"
                    snapshot={row.accepted_invalid_snapshot}
                    stale={row.accepted_invalid_stale}
                    exists={row.accepted_invalid}
                  />
                </div>
              </div>
              {/* 제외 안내는 줄 아래에 둔다. 줄 가운데에 끼우면 뒤의 검증이 다음 줄로 밀린다. 뷰 모드와
                  같은 자리다. */}
              {excludedNote ? <p className={styles.note}>{excludedNote}</p> : null}

              {/* 결함 유형. 비어 있으면 "결함 미기록"이고, 성공을 뜻하지 않는다. `TAG_HOTKEYS` 길이까지는
                  키로도 토글한다. 그래서 칩에 그 키를 표시해 사용자가 키와 칩을 연결할 수 있게 한다. */}
              <div className={`${styles.row} ${styles.tagsRow}`}>
                <AxisLabel ko="결함" param="quality_tag" />
                <QualityTagChips
                  tags={qualityTags}
                  selected={draft.quality_tags}
                  disabled={inputsDisabled}
                  onToggle={onToggleTag}
                />
              </div>

              {/* 표기. 값을 열거할 수 없는 축이므로 편집은 팝오버에서 한다. 독에는 한 줄 요약만
                  두고 CSS 로 줄인다. 값이 문장일 수 있어서(프롬프트) 전체 목록은 툴팁이 보인다. */}
              <div className={`${styles.row} ${styles.termsRow}`}>
                <AxisLabel ko="표기" param="terms" />
                <HatTooltip
                  mode="plain"
                  placement="top"
                  align="start"
                  disabled={termsOpen || draft.terms.length === 0}
                  content={
                    <span className={styles.termLines}>
                      {episodeTermLines(draft.terms).map((line, index) => (
                        <Text key={index} variant={hatcheryTextModes["caption-bold"]}>
                          {line}
                        </Text>
                      ))}
                    </span>
                  }
                >
                  <button
                    ref={termsTriggerRef}
                    type="button"
                    className={styles.popoverTrigger}
                    aria-expanded={termsOpen}
                    aria-haspopup="dialog"
                    onClick={openTerms}
                  >
                    <Keycap>{TERMS_KEY.toUpperCase()}</Keycap>
                    <span className={draft.terms.length === 0 ? styles.triggerEmpty : undefined}>
                      {episodeTermSummary(draft.terms)}
                    </span>
                  </button>
                </HatTooltip>
              </div>

              <CommentRow
                inputRef={commentRef}
                row={row}
                comment={draft.comment}
                length={commentLength}
                maxLength={commentMaxLength}
                tooLong={commentTooLong}
                disabled={inputsDisabled}
                conflicted={conflicted && !loadError}
                onChange={onSetComment}
                onSave={() => {
                  if (!saveDisabled) onSave();
                }}
              />
            </>
          )}
        </div>
      </Scrollbar>

      {/* 표기는 키 수만큼 줄이 늘어서 독에 펼칠 수 없다. 앵커드 팝오버에 넣고, 팝오버 안에서
          키별로 보고 추가 줄로 입력한다. */}
      <AnchoredPopup
        open={active && termsOpen}
        anchor={termsPopup.anchor}
        onClose={closeTerms}
        placement="top-start"
        role="dialog"
        aria-label="에피소드 표기"
        width="30rem"
      >
        <AnchoredPopup.Body padding="md">
          <EpisodeTermEditor
            terms={draft.terms}
            disabled={inputsDisabled}
            onAddTerm={onAddTerm}
            onRemoveTerm={onRemoveTerm}
          />
        </AnchoredPopup.Body>
      </AnchoredPopup>
    </section>
  );
}

/**
 * 검토 상태. 결과와 독립인 축이다. 검토 완료는 마지막으로 완료한 사람과 시각이다. 완료가
 * 해제되었으면 미검토로 표시한다. 해제 전 기록은 활동 로그만 가진다.
 */
function ReviewState({ row }: { row: EpisodeSummary }): ReactNode {
  if (row.reviewed_at === null) {
    return <span className={`${styles.badge} ${styles.badgeUnreviewed}`}>미검토</span>;
  }
  const reviewedAtMs = parseUtcMs(row.reviewed_at);
  const who = row.reviewed_by_member?.display_name;
  return (
    <span className={styles.reviewState} title={`검토 완료 · ${formatDateTime(reviewedAtMs)}`}>
      <span className={`${styles.badge} ${styles.badgeReviewed}`}>검토 완료</span>
      <span className={styles.muted}>
        {who === undefined ? "" : `${who} · `}
        {formatMonthDayTime(reviewedAtMs)}
      </span>
    </span>
  );
}

function validationText(row: EpisodeSummary): string {
  const parts = [EPISODE_VALIDATION_TEXT[row.validation_status]];
  // 표시 전용 접기다. 목록 행(사유 미포함)과 사유 없음은 둘 다 사유 없이 표시한다.
  const reason = row.invalid_reason ?? null;
  if (reason !== null) parts.push(EPISODE_INVALID_REASON_TEXT[reason]);
  if (row.accepted_invalid) parts.push("수용함");
  return parts.join(" · ");
}

// 뷰 모드의 한 줄 요약. 무엇이 적혀 있는지만 알린다(값 자체는 리뷰 모드에서 본다). 편집 중인 값을
// 쓴다.
function reviewSummary(draft: EpisodeReviewDraft): string {
  const parts: string[] = [];
  if (draft.task_failed) parts.push("실패");
  if (draft.quality_tags.length > 0) parts.push(`결함 ${draft.quality_tags.length}`);
  if (draft.accepted_invalid) parts.push("수용함");
  if (draft.comment.trim() !== "") parts.push("코멘트 있음");
  return parts.length === 0 ? "기록 없음" : parts.join(" · ");
}

/**
 * 저장·검토 완료 버튼. 머리 줄 오른쪽 끝 한 곳에만 둔다. 두 모드가 같은 버튼을 쓴다.
 * 수정 내용이 없어도 누를 수 있다. 누르면 검토 완료를 기록한다.
 */
function SaveAction({
  busy,
  dirty,
  disabled,
  onSave,
}: {
  busy: boolean;
  dirty: boolean;
  disabled: boolean;
  onSave: () => void;
}) {
  return (
    <IconButton
      text={busy ? "저장 중…" : "저장·검토 완료"}
      title={`수정 내용을 저장하고 검토 완료로 기록합니다. 수정 내용이 없어도 완료할 수 있습니다. 입력 밖에서는 Enter·${SAVE_KEY.toUpperCase()} 로도 실행합니다.`}
      variant="solid"
      tone="primary"
      className={dirty ? `${styles.save} ${styles.saveDirty}` : styles.save}
      disabled={disabled}
      onClick={onSave}
    >
      <Keycap className={styles.saveKeycap}>⏎</Keycap>
      <Keycap className={styles.saveKeycap}>{SAVE_KEY.toUpperCase()}</Keycap>
    </IconButton>
  );
}

/**
 * 줄 라벨. 형식은 `한글(필터 파라미터)` 다.
 *
 * 괄호 안은 목록 필터 query 파라미터다. 이 병기는 화면에서 좁힌 결과를 MCP·API 응답과 그대로
 * 대조하기 위한 것이다. 파라미터는 칩의 원문 키와 **같은 단계로 옅게 둔다**(`tagKey`).
 */
function AxisLabel({ ko, param, inline }: { ko: string; param: string; inline?: boolean }) {
  return (
    <span className={inline ? styles.rowLabelInline : styles.rowLabel}>
      {ko}
      <span className={styles.tagKey}>({param})</span>
    </span>
  );
}

/**
 * 결함 칩 묶음. 순번 키를 표시한 토글 칩들이다. 어휘가 비면 아무것도 그리지 않는다(옵션 계약을
 * 받지 못한 degrade).
 */
function QualityTagChips({
  tags,
  selected,
  disabled,
  onToggle,
}: {
  // 서버가 준 어휘·순서. 이 배열 위치가 순번 키 순서다.
  tags: readonly string[];
  selected: readonly string[];
  disabled: boolean;
  onToggle: (tag: string) => void;
}): ReactNode {
  // 칩 색의 단계는 테마마다 다르다. 라이트는 어두운 단계, 다크는 밝은 단계여야 12% 배경
  // 위에서 글자가 구분된다(`episodeQualityTagColors.ts`).
  const [themeMode] = useThemeModePreference();
  return (
    <div className={styles.tags}>
      {tags.map((tag, index) => {
        const on = selected.includes(tag);
        const tip = episodeQualityTagTip(tag);
        const text = episodeQualityTagLabel(tag);
        const chip = (
          <LabelChip
            color={episodeQualityTagChipColor(on, themeMode)}
            size="sm"
            variant="interactive"
            className={styles.tag}
            // 순번 키를 컬러 swatch 자리에 넣는다. swatch 자리는 칩의 "아이콘" 자리다.
            // 켜짐/꺼짐은 swatch 가 아니라 칩 색이 나타낸다.
            icon={
              index < TAG_HOTKEYS.length ? (
                <Keycap className={styles.tagKeycap}>{TAG_HOTKEYS[index]?.toUpperCase()}</Keycap>
              ) : undefined
            }
            // 보이는 글자와 같은 문구다. 그러나 스타일 때문에 두 span 으로 나누면 접근성
            // 이름이 `한글 (영어)` 로 벌어진다. 표기 그대로 읽히도록 여기서 고정한다.
            aria-label={episodeQualityTagText(tag)}
            aria-pressed={on}
            disabled={disabled}
            onClick={() => onToggle(tag)}
          >
            {/* 원문 키를 항상 함께 표시한다. 필터·집계·MCP 응답과 비교할 때 키로 대조한다.
                한글이 없는 태그는 라벨이 이미 키이므로 반복하지 않는다. */}
            <span>
              {text}
              {text === tag ? null : <span className={styles.tagKey}>({tag})</span>}
            </span>
          </LabelChip>
        );
        return tip ? (
          <HatTextTooltip key={tag} text={tip} placement="top">
            {chip}
          </HatTextTooltip>
        ) : (
          <span key={tag}>{chip}</span>
        );
      })}
    </div>
  );
}

/**
 * 코멘트 줄. 독 안의 입력 하나다. 입력 안에서는 독 단축키가 동작하지 않는다. `Esc` 로 입력에서
 * 나오고 `Ctrl/⌘+Enter` 로 저장·검토 완료한다. 빈 내용으로 저장하면 코멘트를 지운다.
 */
function CommentRow({
  inputRef,
  row,
  comment,
  length,
  maxLength,
  tooLong,
  disabled,
  conflicted,
  onChange,
  onSave,
}: {
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  row: EpisodeSummary;
  comment: string;
  // 저장할 값(앞뒤 공백 제거)의 글자 수다.
  length: number;
  maxLength: number;
  // 저장이 한도를 넘는 코멘트를 보내는가(`commentExceedsLimit`). 바꾸지 않은 긴 코멘트는 false 다.
  tooLong: boolean;
  disabled: boolean;
  conflicted: boolean;
  onChange: (value: string) => void;
  onSave: () => void;
}): ReactNode {
  const id = useId();
  return (
    <div className={`${styles.row} ${styles.commentRow}`}>
      <label htmlFor={id} className={styles.rowLabel}>
        코멘트<span className={styles.tagKey}>(comment)</span>
      </label>
      <div className={styles.commentBlock}>
        {conflicted ? (
          <p className={styles.serverComment}>현재 저장된 코멘트: {row.review_comment ?? "없음"}</p>
        ) : null}
        <div className={styles.commentInput}>
          <textarea
            ref={inputRef}
            id={id}
            className={styles.comment}
            rows={2}
            value={comment}
            disabled={disabled}
            aria-describedby={`${id}-count ${id}-meta`}
            aria-invalid={tooLong}
            placeholder={`검토 내용을 남겨 주세요. ${COMMENT_KEY.toUpperCase()} 로 이 입력에 들어옵니다.`}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                // 창 셸의 Esc(창 닫기)는 raw 가드라 defaultPrevented 를 보지 않는다. 여기서 전파를
                // 끊어야 입력에서 나오는 것으로 끝난다.
                event.preventDefault();
                event.stopPropagation();
                event.currentTarget.blur();
                return;
              }
              if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
                event.preventDefault();
                onSave();
              }
            }}
          />
          <span
            id={`${id}-count`}
            className={`${styles.commentCount} ${tooLong ? styles.warning : styles.note}`}
          >
            {length.toLocaleString()} / {maxLength.toLocaleString()}
          </span>
        </div>
        <div id={`${id}-meta`} className={styles.commentMeta}>
          <SnapshotNote
            label="코멘트"
            snapshot={row.review_comment_snapshot}
            stale={row.review_comment_stale}
            exists={row.review_comment !== null}
          />
        </div>
        {tooLong ? (
          <p className={styles.warning} role="alert">
            코멘트를 {maxLength.toLocaleString()}자 이내로 줄여 주세요.
          </p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * 수용·코멘트를 기록한 시각과, 그 뒤 에피소드 상태가 바뀌었다는 경고.
 *
 * 스냅샷은 상세 조회에만 있다. 목록 행에는 필드가 없다(undefined). 그때는 아무것도 그리지 않는다.
 * null 은 기록 당시의 비교 정보가 없다는 뜻이다.
 */
function SnapshotNote({
  label,
  snapshot,
  stale,
  exists,
}: {
  label: string;
  snapshot: EpisodeSnapshot | null | undefined;
  stale: boolean | null | undefined;
  exists: boolean;
}): ReactNode {
  if (!exists || snapshot === undefined) return null;
  return (
    <span className={styles.snapshot}>
      <span className={styles.note}>
        {snapshot === null
          ? `${label} 시점의 비교 기록이 없습니다.`
          : `${label} 기록: ${formatDateTime(parseUtcMs(snapshot.captured_at))}`}
      </span>
      {stale === true ? (
        <span className={styles.warning}>
          {label} 저장 후 에피소드 상태가 바뀌었습니다. 다시 확인해 주세요.
        </span>
      ) : null}
    </span>
  );
}
